import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EditChildModal from '../components/EditChildModal';

const { FiPlus, FiEye, FiSettings, FiUsers, FiCalendar, FiGift, FiBarChart3 } = FiIcons;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeInOut"
    }
  }
};

const statsVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "backOut"
    }
  }
};

const Dashboard = () => {
  const { children } = useAppContext();
  const [editingChild, setEditingChild] = useState(null);

  const totalActiveStreaks = children.reduce((sum, child) => sum + child.currentStreak, 0);
  const totalNights = children.reduce((sum, child) => sum + child.totalGoodNights, 0);
  const totalRewards = children.reduce((sum, child) => sum + child.earnedRewards.length, 0);
  const bestStreak = Math.max(...children.map(child => child.bestStreak), 0);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div 
        variants={cardVariants}
        className="flex justify-between items-center"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Family Dashboard</h1>
          <p className="text-gray-600 mt-1">Track your family's sleep habits and celebrate achievements</p>
        </div>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link
            to="/add-child"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Add Child</span>
          </Link>
        </motion.div>
      </motion.div>

      {/* Family Members Section */}
      <div>
        <motion.div 
          variants={cardVariants}
          className="flex items-center space-x-3 mb-6"
        >
          <SafeIcon icon={FiUsers} className="text-2xl text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Family Members</h2>
          <motion.span 
            variants={statsVariants}
            className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium"
          >
            {children.length}
          </motion.span>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {children.map((child) => (
            <motion.div
              key={child.id}
              variants={cardVariants}
              whileHover="hover"
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative"
            >
              {/* Settings Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setEditingChild(child)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiSettings} className="text-lg" />
              </motion.button>

              {/* Avatar and Info */}
              <div className="text-center mb-4">
                <motion.div 
                  whileHover={{ rotate: 5 }}
                  className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center text-2xl mx-auto mb-3"
                >
                  {child.avatarEmoji}
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-900">{child.name}</h3>
                <p className="text-gray-500">Age {child.age}</p>
              </div>

              {/* Stats */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center space-x-2">
                    <span>🔥</span>
                    <span>Current streak</span>
                  </span>
                  <motion.span 
                    key={child.currentStreak}
                    initial={{ scale: 1.2, color: "#f59e0b" }}
                    animate={{ scale: 1, color: "#374151" }}
                    className="font-semibold"
                  >
                    {child.currentStreak} nights
                  </motion.span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center space-x-2">
                    <span>🌙</span>
                    <span>Total good nights</span>
                  </span>
                  <span className="font-semibold">{child.totalGoodNights} nights</span>
                </div>
              </div>

              {/* Reward Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Next reward at {child.nextReward.target} nights</span>
                  <span>{child.totalGoodNights}/{child.nextReward.target}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((child.totalGoodNights / child.nextReward.target) * 100, 100)}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{child.nextReward.reward}</p>
              </div>

              {/* View Sleep Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to={`/child/${child.id}`}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiEye} />
                  <span>View Sleep</span>
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Family Insights */}
      <motion.div variants={cardVariants}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Family Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div 
            variants={statsVariants}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Family Overview</p>
                <p className="text-sm text-blue-200">Combined family stats</p>
              </div>
              <SafeIcon icon={FiUsers} className="text-3xl text-blue-200" />
            </div>
          </motion.div>

          <motion.div 
            variants={statsVariants}
            whileHover={{ scale: 1.05 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="text-center">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="text-3xl font-bold text-gray-900"
              >
                {totalActiveStreaks}
              </motion.div>
              <p className="text-gray-600">Total Active Streaks</p>
              <p className="text-xs text-gray-500">nights combined</p>
            </div>
          </motion.div>

          <motion.div 
            variants={statsVariants}
            whileHover={{ scale: 1.05 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="text-center">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="text-3xl font-bold text-gray-900"
              >
                {totalNights}
              </motion.div>
              <p className="text-gray-600">Total Nights Logged</p>
              <p className="text-xs text-gray-500">across all children</p>
            </div>
          </motion.div>

          <motion.div 
            variants={statsVariants}
            whileHover={{ scale: 1.05 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="text-center">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                className="text-3xl font-bold text-gray-900"
              >
                {totalRewards}
              </motion.div>
              <p className="text-gray-600">Rewards Earned</p>
              <p className="text-xs text-gray-500">milestones achieved</p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Edit Child Modal */}
      {editingChild && (
        <EditChildModal
          child={editingChild}
          isOpen={!!editingChild}
          onClose={() => setEditingChild(null)}
        />
      )}
    </motion.div>
  );
};

export default Dashboard;